import { useContext } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrashAlt } from '@fortawesome/pro-light-svg-icons'

import {
  Button, Form, ModalContext, SidebarModal, SidebarNoticeBox,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import { confirmDelete } from '@functions/entity'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import useDataStoreItem, { useDataStoreItemForm } from '@hooks/useDataStoreItem'

import defaultRequestOptions from '@sections/Client/packs/Campaign/defaultRequestOptions'

import SecuredText from './components/SecuredText'

const MODAL_KEY = 'EditDataStoreItemModal'

export type ModalCallbacks = {
  closeModal: () => void,
  createActivity: (payload: HandleCallbackActionParams) => void,
  updateDataStoreItem: (payload: HandleCallbackActionParams) => void,
}

interface ModalProps {
  callbacks: ModalCallbacks,
  modalKey?: string,
  showModal: boolean,
}

const EditDataStoreItemModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, updateDataStoreItem } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const initDataStoreItem = digObject(modalPayload, 'dataStoreItem', {})

  const dataStoreItemPayload = useDataStoreItem(initDataStoreItem)

  const {
    callbacks: {
      deleteDataStoreItem: deleteFn,
      updateDataStoreItem: updateFn,
    },
    dataStoreItem,
    deleting,
    digitalTemplate,
    secured,
    updating,
    user,
  } = dataStoreItemPayload

  const dataStoreItemFormPayload = useDataStoreItemForm(dataStoreItem, { validateOn: 'change' })

  const {
    entityState,
    entityState: {
      key,
      label,
    },
    errors,
    handlers,
    saveEnabled,
  } = dataStoreItemFormPayload

  const updateDataStoreItemPayload = {
    callbacks: {
      action: updateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    requestOptions: defaultRequestOptions.dataStoreItem,
    toastText: 'Data Store Item Updated',
  }

  const confirmDeletePayload = {
    callbacks: {
      afterAction: closeModal,
      deleteFn,
    },
    strings: { entityName: 'Data Store Item' },
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal} size="small">
      <SidebarModal.Header callbacks={callbacks} title="Edit" titleSecondLine="Data Store" />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="Label">
            <input type="text" name="label" value={label} {...handlers} />
          </Form.Field>

          <Form.Field errorMessage={errors.key} label="Key" marginTop="large">
            <input type="text" name="key" value={key} {...handlers} />
          </Form.Field>

          {initDataStoreItem.key !== key && (
            <SidebarNoticeBox boxProps={{ marginTop: 'large', marginBottom: 0 }}>
              <SidebarNoticeBox.Title>Import Notice</SidebarNoticeBox.Title>
              <SidebarNoticeBox.Paragraph>
                Changing the key may affect your digital pages.
              </SidebarNoticeBox.Paragraph>
            </SidebarNoticeBox>
          )}

          <SecuredText
            callbacks={callbacks}
            dataStoreItemFormPayload={dataStoreItemFormPayload}
            secured={secured}
          />

          <Form.Field label="Source" marginTop="large">
            <input disabled type="text" value={digitalTemplate.title || 'Unknown Template'} />
          </Form.Field>

          <Form.Field label="Last Edited By" marginTop="large">
            <input disabled type="text" value={user.full_name || 'Unknown User'} />
          </Form.Field>

          <Form.Field label="More Options" marginTop="large">
            <Button
              buttonStyle="secondaryUtility"
              loading={deleting}
              onClick={() => confirmDelete(confirmDeletePayload)}
              icon={<FontAwesomeIcon icon={faTrashAlt} />}
              size="medium"
            >
              Delete Record
            </Button>
          </Form.Field>
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={updating}
          onClick={() => updateDataStoreItem(updateDataStoreItemPayload)}
          size="large"
        >
          Save Details
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <EditDataStoreItemModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
